/**
 * Cportal Component
 * Renders children into a portal at document body level
 * Used for dropdowns, modals, and other overlays that need to escape parent overflow constraints
 */

'use client';

import { useCallback, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * The app's SHARED portal container: every rising modal renders inside it, so
 * anything that must clear a modal while staying inside the same container
 * mounts here and orders itself with an in-container z-index.
 */
export const PORTAL_ROOT_CONTAINER_ID = 'portal-root';
/** The z-index the shared container element itself carries on `<body>`. */
export const PORTAL_ROOT_Z_INDEX = 9999;
const CPORTAL_POSITION_ABSOLUTE = 'absolute';
const CPORTAL_OFFSET_ZERO = '0';
const getServerPortalSnapshot = (): HTMLElement | null => null;

export enum PortalLifecyclePhase {
  Mount = 'mount',
  Cleanup = 'cleanup',
}

export interface CportalProps {
  children: ReactNode;
  containerId?: string;
  /**
   * Z-index applied to the portal container element itself.
   * Pass this when the content must escape an existing stacking context
   * (e.g. a modal at z-index 9999). Defaults to 9999.
   * Each distinct value gets its own container so callers don't interfere.
   */
  containerZIndex?: number;
  /**
   * TEMPORARY whole-modal flicker probe hook — fires on portal effect mount and
   * cleanup so a portal-container teardown (which removes children with no React
   * state change / exit animation) is observable. Remove with flicker-probe.ts.
   * Pass a STABLE (memoized) callback to avoid re-running the container effect.
   */
  onPortalLifecycle?: (event: {
    phase: PortalLifecyclePhase;
    containerId: string;
    containerExisted: boolean;
  }) => void;
}

interface PortalContainerSubscriptionInput {
  containerId: string;
  zIndex: number;
  onPortalLifecycle?: CportalProps['onPortalLifecycle'];
}

function getPortalContainerSnapshot(containerId: string): HTMLElement | null {
  return document.getElementById(containerId);
}

function subscribeToPortalContainer({
  containerId,
  zIndex,
  onPortalLifecycle,
}: PortalContainerSubscriptionInput, notifyContainerReady: () => void): () => void {
  let container = document.getElementById(containerId);
  const containerExisted = Boolean(container);

  if (!container) {
    container = document.createElement('div');
    container.id = containerId;
    container.style.position = CPORTAL_POSITION_ABSOLUTE;
    container.style.top = CPORTAL_OFFSET_ZERO;
    container.style.left = CPORTAL_OFFSET_ZERO;
    container.style.zIndex = String(zIndex);
    document.body.appendChild(container);
  }

  notifyContainerReady();
  onPortalLifecycle?.({
    phase: PortalLifecyclePhase.Mount,
    containerId,
    containerExisted,
  });

  return () => {
    onPortalLifecycle?.({
      phase: PortalLifecyclePhase.Cleanup,
      containerId,
      containerExisted: true,
    });
  };
}

export function Cportal({
  children,
  containerId,
  containerZIndex,
  onPortalLifecycle,
}: CportalProps) {
  const effectiveZIndex = containerZIndex ?? PORTAL_ROOT_Z_INDEX;
  const effectiveContainerId = containerId ?? PORTAL_ROOT_CONTAINER_ID;

  const subscribe = useCallback(
    (notifyContainerReady: () => void) =>
      subscribeToPortalContainer({
        containerId: effectiveContainerId,
        zIndex: effectiveZIndex,
        onPortalLifecycle,
      }, notifyContainerReady),
    [effectiveContainerId, effectiveZIndex, onPortalLifecycle],
  );
  const getSnapshot = useCallback(
    () => getPortalContainerSnapshot(effectiveContainerId),
    [effectiveContainerId],
  );
  const portalContainer = useSyncExternalStore(subscribe, getSnapshot, getServerPortalSnapshot);

  if (!portalContainer) {
    return null;
  }

  return createPortal(children, portalContainer);
}
